import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { getBlogPost, initializeBlogSystem } from '../database/blogStore';
import ReactMarkdown from 'react-markdown';
import { formatDate } from '../utils/dateUtils';
import { SEOHead } from '../components';
import './BlogDetail.css';
import {
    Clock,
    User,
    Calendar,
    ArrowLeft,
    Lightbulb,
    Share2,
    Twitter,
    Linkedin,
    Facebook,
    ChevronRight,
    Mail,
    Target,
    ArrowRight
} from 'lucide-react';

// Get estimated reading time
const getReadingTime = (content) => {
    const wordsPerMinute = 200;
    const wordCount = content.trim().split(/\s+/).length;
    let baseReadingTime = Math.ceil(wordCount / wordsPerMinute);

    // Ensure reading time is between 1-3 minutes
    if (baseReadingTime < 1) baseReadingTime = 1;
    if (baseReadingTime > 3) baseReadingTime = 3;

    return baseReadingTime;
};

// Get excerpt from content
const getExcerpt = (content, maxLength = 160) => {
    if (!content) return '';
    
    // Remove markdown headers
    const withoutHeaders = content.replace(/^#+ .*$/gm, '').trim();
    
    // Get first paragraph
    const firstParagraph = withoutHeaders.split('\n\n')[0];
    
    if (firstParagraph.length <= maxLength) return firstParagraph;
    
    return firstParagraph.substring(0, maxLength) + '...';
};

// Extract executive summary from content
const extractExecutiveSummary = (content) => {
    const summaryMatch = content.match(/## Executive Summary\n\n([^#]+)/);
    if (!summaryMatch) return null;

    const summary = {
        overview: '',
        businessContext: '',
        keyFindings: '',
        strategicImplications: ''
    };

    const summaryText = summaryMatch[1];
    const sections = summaryText.split('\n\n');

    sections.forEach(section => {
        if (section.startsWith('Overview:')) {
            summary.overview = section.replace('Overview:', '').trim();
        } else if (section.startsWith('Business Context:')) {
            summary.businessContext = section.replace('Business Context:', '').trim();
        } else if (section.startsWith('Key Findings:')) {
            summary.keyFindings = section.replace('Key Findings:', '').trim();
        } else if (section.startsWith('Strategic Implications:')) {
            summary.strategicImplications = section.replace('Strategic Implications:', '').trim();
        }
    });

    return summary;
};

// Extract key takeaways and recommendations
const extractKeyTakeaways = (content) => {
    const sections = {
        takeaways: [],
        recommendations: [],
        nextSteps: []
    };

    const takeawaysMatch = content.match(/## Key Takeaways\n\n([^#]+)/);
    const recommendationsMatch = content.match(/## Strategic Recommendations\n\n([^#]+)/);
    const nextStepsMatch = content.match(/## Next Steps\n\n([^#]+)/);

    if (takeawaysMatch) {
        sections.takeaways = takeawaysMatch[1]
            .split('\n')
            .filter(line => line.startsWith('- '))
            .map(line => line.replace('- ', '').trim());
    }

    if (recommendationsMatch) {
        sections.recommendations = recommendationsMatch[1]
            .split('\n')
            .filter(line => line.startsWith('- '))
            .map(line => line.replace('- ', '').trim());
    }

    if (nextStepsMatch) {
        sections.nextSteps = nextStepsMatch[1]
            .split('\n')
            .filter(line => line.startsWith('- '))
            .map(line => line.replace('- ', '').trim());
    }

    return sections;
};

// Transform content for executive-facing rendering: remove long sections and tune investment copy
const cleanMainContent = (content) => {
    let result = content;
    // Remove executive summary block if present
    result = result.replace(/## Executive Summary[\s\S]*?(?=## |\Z)/, '');
    // Remove key takeaways block (we render curated takeaways separately)
    result = result.replace(/## Key Takeaways[\s\S]*?(?=## |\Z)/, '');
    // Remove overly technical requirements section
    result = result.replace(/## Technology Requirements[\s\S]*?(?=## |\Z)/, '');
    // Replace investment economics with concise snapshot
    result = result.replace(
        /## Investment Economics[\s\S]*?(?=## |\Z)/,
        '## Investment Snapshot\n\n- Pilot: $25k–$75k (4–6 weeks)\n- Scale-up (per business unit): $120k–$350k\n- Break-even: 6–12 weeks\n- 12-month ROI: 3–7x\n\n'
    );
    // Collapse excessive blank lines
    result = result.replace(/\n{3,}/g, '\n\n');
    return result.trim();
};

const BlogDetail = () => {
    const { identifier } = useParams();
    const [post, setPost] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [relatedPosts, setRelatedPosts] = useState([]);
    const [executiveSummary, setExecutiveSummary] = useState(null);
    const [keyTakeaways, setKeyTakeaways] = useState(null);
    const [mainContent, setMainContent] = useState('');
    const { theme } = useTheme();

    useEffect(() => {
        const loadPost = async () => {
            try {
                setIsLoading(true);
                await initializeBlogSystem();
                const loadedPost = await getBlogPost(identifier);

                // Extract executive summary and key takeaways
                const summary = extractExecutiveSummary(loadedPost.content);
                const takeaways = extractKeyTakeaways(loadedPost.content);
                const cleanedContent = cleanMainContent(loadedPost.content);

                setPost(loadedPost);
                setExecutiveSummary(summary);
                setKeyTakeaways(takeaways);
                setMainContent(cleanedContent);

                // Fetch related posts based on tags
                if (loadedPost.tags && loadedPost.tags.length > 0) {
                    const allPosts = await getBlogPost();
                    const related = allPosts
                        .filter(p => p.id !== id)
                        .filter(p => p.tags && p.tags.some(tag => loadedPost.tags.includes(tag)))
                        .slice(0, 3);

                    setRelatedPosts(related);
                }
            } catch (error) {
                console.error('Error loading blog post:', error);
            } finally {
                setIsLoading(false);
            }
        };

        loadPost();
    }, [identifier]);

    const canonicalSlug = post?.slug || post?.id;
    const canonicalUrl = canonicalSlug
        ? `https://sharedoxygen.com/blog/${canonicalSlug}`
        : window.location.href;
    const shareTitle = post?.title;

    const handleShare = (platform) => {
        const urls = {
            twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareTitle)}&url=${encodeURIComponent(canonicalUrl)}`,
            linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(canonicalUrl)}`,
            facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(canonicalUrl)}`,
            email: `mailto:?subject=${encodeURIComponent(shareTitle)}&body=${encodeURIComponent(canonicalUrl)}`
        };
        window.open(urls[platform], '_blank');
    };

    if (isLoading) {
        return (
            <div className={`min-h-screen flex items-center justify-center ${theme === 'dark' ? 'bg-gray-900' : 'bg-white'}`}>
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
            </div>
        );
    }

    if (!post) {
        return (
            <div className={`min-h-screen flex items-center justify-center ${theme === 'dark' ? 'bg-gray-900' : 'bg-gray-50'}`}>
                <div className="text-center">
                    <h2 className={`text-2xl mb-4 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>Blog Post Not Found</h2>
                    <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>The blog post you're looking for doesn't exist or has been removed.</p>
                    <Link to="/blog" className={`mt-4 inline-block ${theme === 'dark' ? 'text-blue-400 hover:text-blue-300' : 'text-primary-600 hover:text-primary-700'}`}>
                        Return to Blog
                    </Link>
                </div>
            </div>
        );
    }

    const readingTime = getReadingTime(post.content);

    // Add dark theme class when dark mode is active
    const bodyClass = theme === 'dark' ? 'dark-theme' : 'light-theme';

    return (
        <div className={`min-h-screen pb-16 ${bodyClass} ${theme === 'dark' ? 'bg-gray-900' : 'bg-gray-50'}`}>
            {/* SEO Meta Tags */}
            <SEOHead
                title={post.title}
                description={post.excerpt || getExcerpt(post.content, 160)}
                image={post.imageUrl}
                url={canonicalUrl}
                type="article"
                author="Shared Oxygen"
                publishedTime={post.publishedDate}
                modifiedTime={post.updatedAt}
                tags={post.tags || []}
            />
            
            {/* Hero Section */}
            <div className="blog-hero">
                <img
                    src={post.imageUrl || '/images/blog/default.jpg'}
                    alt={post.title}
                    className="w-full h-full object-cover"
                />
                <div className="blog-hero-content absolute inset-0 p-8 flex flex-col justify-end">
                    <div className="max-w-4xl mx-auto w-full">
                        <nav className="mb-8 flex items-center text-white/80">
                            <Link to="/blog" className="hover:text-white transition-colors">Blog</Link>
                            <ChevronRight className="w-4 h-4 mx-2" />
                            <span className="text-white/60">{post.title}</span>
                        </nav>
                        <div className="flex flex-wrap gap-2 mb-6">
                            {post.tags?.map((tag, index) => (
                                <span
                                    key={index}
                                    className="px-3 py-1 bg-white/10 backdrop-blur-sm rounded-full text-sm font-medium"
                                >
                                    {tag}
                                </span>
                            ))}
                        </div>
                        <h1 className="blog-hero-title mb-6">{post.title}</h1>
                        <div className="blog-hero-meta flex items-center gap-6 text-sm">
                            <div className="flex items-center">
                                <User className="w-4 h-4 mr-2" />
                                <span className="font-medium">Shared Oxygen</span>
                            </div>
                            <div className="flex items-center">
                                <Calendar className="w-4 h-4 mr-2" />
                                <span>{formatDate(post.publishedDate || post.createdAt)}</span>
                            </div>
                            <div className="flex items-center">
                                <Clock className="w-4 h-4 mr-2" />
                                <span>{readingTime} min read</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-4xl mx-auto px-4">
                {/* Executive Summary removed for marketing brevity */}

                {/* Full-width feature image (LinkedIn/Medium style) */}
                {post.imageUrl && (
                    <div className="my-10">
                        <div className="overflow-hidden rounded-2xl shadow-xl ring-1 ring-black/5">
                            <img
                                src={post.imageUrl}
                                alt={post.title}
                                className="w-full h-full object-cover"
                            />
                        </div>
                        <p className={`mt-3 text-center text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                            AI Flight Deck · Signal Integrity · Judgment Velocity · Cycle Lift
                        </p>
                    </div>
                )}

                {/* Key Insights Section */}
                {keyTakeaways && (
                    <>
                        {keyTakeaways.takeaways.length > 0 && (
                            <div className="key-takeaways mb-8">
                                <div className="flex items-center mb-4">
                                    <Lightbulb className={`w-6 h-6 ${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'}`} />
                                    <h3 className="text-xl font-semibold ml-3">Key Takeaways</h3>
                                </div>
                                <ul>
                                    {keyTakeaways.takeaways.map((takeaway, index) => (
                                        <li key={index}>{takeaway}</li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {keyTakeaways.recommendations.length > 0 && (
                            <div className="key-takeaways mb-8">
                                <div className="flex items-center mb-4">
                                    <Target className={`w-6 h-6 ${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'}`} />
                                    <h3 className="text-xl font-semibold ml-3">Strategic Recommendations</h3>
                                </div>
                                <ul>
                                    {keyTakeaways.recommendations.map((rec, index) => (
                                        <li key={index}>{rec}</li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {keyTakeaways.nextSteps.length > 0 && (
                            <div className="key-takeaways mb-8">
                                <div className="flex items-center mb-4">
                                    <ArrowRight className={`w-6 h-6 ${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'}`} />
                                    <h3 className="text-xl font-semibold ml-3">Next Steps</h3>
                                </div>
                                <ul>
                                    {keyTakeaways.nextSteps.map((step, index) => (
                                        <li key={index}>{step}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </>
                )}

                {/* Main Content */}
                <div className="blog-content">
                    <ReactMarkdown>{mainContent}</ReactMarkdown>
                </div>

                {/* Author Section */}
                {/* <div className="blog-author">
                    <div className="blog-author-header">
                        <img
                            src="/images/admin-avatar.jpg"
                            alt="Shared Oxygen"
                            className="blog-author-avatar"
                            onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = '/images/default-avatar.jpg';
                            }}
                        />
                        <div>
                            <h3 className="blog-author-name">Shared Oxygen</h3>
                        </div>
                    </div>
                    <p className="blog-author-bio">
                        Our expertise spans artificial intelligence, modern development approaches,
                        and digital transformation strategies. We help organizations leverage cutting-edge
                        technology to drive business value and innovation.
                    </p>
                </div> */}

                {/* Share Section */}
                <div className="blog-share">
                    <div className="flex flex-col items-center">
                        <h3 className={`text-lg font-semibold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Share this article</h3>
                        <div className="flex gap-4">
                            <button
                                onClick={() => handleShare('twitter')}
                                className={`p-2 rounded-full transition-colors ${theme === 'dark'
                                        ? 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                                        : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                                    }`}
                                aria-label="Share on Twitter"
                            >
                                <Twitter className="w-5 h-5" />
                            </button>
                            <button
                                onClick={() => handleShare('linkedin')}
                                className={`p-2 rounded-full transition-colors ${theme === 'dark'
                                        ? 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                                        : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                                    }`}
                                aria-label="Share on LinkedIn"
                            >
                                <Linkedin className="w-5 h-5" />
                            </button>
                            <button
                                onClick={() => handleShare('facebook')}
                                className={`p-2 rounded-full transition-colors ${theme === 'dark'
                                        ? 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                                        : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                                    }`}
                                aria-label="Share on Facebook"
                            >
                                <Facebook className="w-5 h-5" />
                            </button>
                            <button
                                onClick={() => handleShare('email')}
                                className={`p-2 rounded-full transition-colors ${theme === 'dark'
                                        ? 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                                        : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                                    }`}
                                aria-label="Share via Email"
                            >
                                <Mail className="w-5 h-5" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Related Posts */}
            {relatedPosts.length > 0 && (
                <div className="blog-related">
                    <h2 className="blog-related-title">Related Articles</h2>
                    <div className="blog-related-grid">
                        {relatedPosts.map(relatedPost => (
                            <Link
                                key={relatedPost.id}
                                to={`/blog/${relatedPost.slug || relatedPost.id}`}
                                className={`rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-all group ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'
                                    }`}
                            >
                                <div className="aspect-video relative overflow-hidden">
                                    <img
                                        src={relatedPost.imageUrl || '/images/blog/default.jpg'}
                                        alt={relatedPost.title}
                                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
                                </div>
                                <div className="p-4">
                                    <h3 className={`font-bold mb-2 transition-colors ${theme === 'dark'
                                            ? 'text-white group-hover:text-blue-300'
                                            : 'text-gray-800 group-hover:text-blue-600'
                                        }`}>
                                        {relatedPost.title}
                                    </h3>
                                    <p className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                                        {formatDate(relatedPost.publishedDate || relatedPost.createdAt)}
                                    </p>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default BlogDetail; 